import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { LinearProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Types
export interface ParticipantUploadStatus {
    id: string;
    name: string;
    uploadProgress: number;
    isComplete: boolean;
    isHost?: boolean;
}

interface RecordingStoppedOverlayProps {
    participants: ParticipantUploadStatus[];
    onClose: () => void;
    onNavigate?: () => void;
    isHost: boolean;
}

// Shared styles following design system - calm, editorial, content-first
const overlayContainerStyle = {
    position: 'absolute' as const,
    bottom: 24,
    left: 24,
    zIndex: 100,
    display: 'flex',
    width: '340px',
    maxHeight: '400px',
    flexDirection: 'column' as const,
    borderRadius: '12px',
    backgroundColor: '#ffffff',
    boxShadow: '0px 20px 60px rgba(15, 23, 42, 0.08)',
    border: '1px solid #E5E7EB',
    overflow: 'hidden',
};

const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px',
    borderBottom: '1px solid #F3F4F6',
};

const CloseIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none">
        <path d="M17 7L7 17" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M7 7L17 17" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

// Upload icon - simple and professional
const UploadIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none">
        <path d="M12 16V4M12 4L8 8M12 4L16 8" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M4 17V19C4 20.1046 4.89543 21 6 21H18C19.1046 21 20 20.1046 20 19V17" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

// Individual participant upload row
const ParticipantUploadRow: React.FC<{ participant: ParticipantUploadStatus; showProgress: boolean }> = ({ participant, showProgress }) => {
    const isComplete = participant.isComplete;  // Only use uploadCompleted from backend, not uploadProgress
    const displayProgress = showProgress ? participant.uploadProgress : 0;
    
    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            padding: '10px 0',
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Typography sx={{
                        fontSize: '13px',
                        fontWeight: 500,
                        color: '#374151',
                        fontFamily: 'Inter, sans-serif',
                    }}>
                        {participant.name}
                    </Typography>
                    {participant.isHost && (
                        <Box sx={{
                            px: 0.5,
                            py: 0.125,
                            borderRadius: '3px',
                            backgroundColor: '#F3F4F6',
                            fontSize: '10px',
                            fontWeight: 500,
                            color: '#6B7280',
                            fontFamily: 'Inter, sans-serif',
                            letterSpacing: '0.02em',
                        }}>
                            You
                        </Box>
                    )}
                </Box>
                {showProgress ? (
                    isComplete ? (
                        <CheckCircleIcon sx={{ fontSize: 16, color: '#22C55E' }} />
                    ) : (
                        <Typography sx={{
                            fontSize: '12px',
                            fontWeight: 500,
                            color: '#9CA3AF',
                            fontFamily: 'Inter, sans-serif',
                        }}>
                            {Math.round(displayProgress)}%
                        </Typography>
                    )
                ) : (
                    <Box sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        border: '2px solid #E5E7EB',
                        borderTopColor: '#9CA3AF',
                        animation: 'spin 1s linear infinite',
                        '@keyframes spin': {
                            '0%': { transform: 'rotate(0deg)' },
                            '100%': { transform: 'rotate(360deg)' },
                        },
                    }} />
                )}
            </Box>
            {!isComplete && showProgress && (
                <LinearProgress
                    variant="determinate"
                    value={displayProgress}
                    sx={{
                        height: 4,
                        borderRadius: 2,
                        backgroundColor: '#F3F4F6',
                        '& .MuiLinearProgress-bar': {
                            backgroundColor: '#2B6BFD',
                            borderRadius: 2,
                        },
                    }}
                />
            )}
        </Box>
    );
};

/**
 * Host Recording Stopped Overlay
 * Shows upload progress for all participants (host + guests)
 * Calm, editorial design following Libretto design system
 */
export const HostRecordingStoppedOverlay: React.FC<RecordingStoppedOverlayProps> = ({
    participants,
    onClose,
    onNavigate,
}) => {
    // Delay showing progress to avoid showing 100% prematurely when chunks are being prepared
    const [showProgress, setShowProgress] = useState(false);
    
    useEffect(() => {
        const timer = setTimeout(() => {
            setShowProgress(true);
        }, 600);
        return () => clearTimeout(timer);
    }, []);

    const allComplete = showProgress && participants.every(p => p.isComplete);  // Only use uploadCompleted from backend

    return (
        <Box sx={overlayContainerStyle}>
            {/* Header */}
            <Box sx={headerStyle}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {allComplete ? (
                        <CheckCircleIcon sx={{ fontSize: 18, color: '#22C55E' }} />
                    ) : (
                        <UploadIcon />
                    )}
                    <Typography sx={{
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#1F2937',
                        fontFamily: 'Inter, sans-serif',
                        letterSpacing: '-0.01em',
                    }}>
                        {allComplete ? 'Upload Complete' : 'Uploading'}
                    </Typography>
                </Box>
                <Box 
                    onClick={onClose}
                    sx={{ 
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 26,
                        height: 26,
                        borderRadius: '6px',
                        transition: 'background-color 0.15s ease',
                        '&:hover': {
                            backgroundColor: '#F3F4F6',
                        },
                    }}
                >
                    <CloseIcon />
                </Box>
            </Box>

            {/* Content */}
            <Box sx={{ 
                padding: '12px 16px 14px',
                maxHeight: '260px',
                overflow: 'auto',
            }}>
                {/* Status message */}
                <Typography sx={{
                    fontSize: '13px',
                    color: '#6B7280',
                    fontFamily: 'Inter, sans-serif',
                    marginBottom: '10px',
                    lineHeight: '1.5',
                }}>
                    {allComplete 
                        ? 'All recordings uploaded. Processing will begin shortly.'
                        : 'Keep this page open until uploads complete.'}
                </Typography>

                {/* Participant list */}
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {participants.map((participant, index) => (
                        <React.Fragment key={participant.id}>
                            <ParticipantUploadRow participant={participant} showProgress={showProgress} />
                            {index < participants.length - 1 && (
                                <Box sx={{ height: '1px', backgroundColor: '#F3F4F6' }} />
                            )}
                        </React.Fragment>
                    ))}
                </Box>
            </Box>

            {/* Footer with action button (when complete) */}
            {allComplete && onNavigate && (
                <Box sx={{
                    padding: '10px 16px 14px',
                    borderTop: '1px solid #F3F4F6',
                }}>
                    <Box
                        onClick={onNavigate}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '9px 14px',
                            borderRadius: '8px',
                            backgroundColor: '#1A1A1A',
                            cursor: 'pointer',
                            transition: 'opacity 0.15s ease',
                            '&:hover': {
                                opacity: 0.85,
                            },
                        }}
                    >
                        <Typography sx={{
                            fontSize: '13px',
                            fontWeight: 500,
                            color: '#ffffff',
                            fontFamily: 'Inter, sans-serif',
                        }}>
                            View Recording
                        </Typography>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

/**
 * Guest Recording Stopped Overlay
 * Shows only the guest's own upload progress
 */
interface GuestRecordingStoppedOverlayProps {
    uploadProgress: number;
    isComplete: boolean;  // From uploadCompleted field in Firestore
    onClose: () => void;
}

export const GuestRecordingStoppedOverlay: React.FC<GuestRecordingStoppedOverlayProps> = ({
    uploadProgress,
    isComplete: isUploadComplete,
    onClose,
}) => {
    // Delay showing progress to avoid showing 100% prematurely
    const [showProgress, setShowProgress] = useState(false);
    
    useEffect(() => {
        const timer = setTimeout(() => {
            setShowProgress(true);
        }, 600);
        return () => clearTimeout(timer);
    }, []);

    const isComplete = showProgress && isUploadComplete;  // Only use uploadCompleted from backend
    const displayProgress = showProgress ? uploadProgress : 0;

    return (
        <Box sx={overlayContainerStyle}>
            {/* Header */}
            <Box sx={headerStyle}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {isComplete ? (
                        <CheckCircleIcon sx={{ fontSize: 18, color: '#22C55E' }} />
                    ) : (
                        <UploadIcon />
                    )}
                    <Typography sx={{
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#1F2937',
                        fontFamily: 'Inter, sans-serif',
                        letterSpacing: '-0.01em',
                    }}>
                        {isComplete ? 'Upload Complete' : 'Uploading'}
                    </Typography>
                </Box>
                <Box 
                    onClick={onClose}
                    sx={{ 
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 26,
                        height: 26,
                        borderRadius: '6px',
                        transition: 'background-color 0.15s ease',
                        '&:hover': {
                            backgroundColor: '#F3F4F6',
                        },
                    }}
                >
                    <CloseIcon />
                </Box>
            </Box>

            {/* Content */}
            <Box sx={{ padding: '14px 16px 16px' }}>
                {isComplete ? (
                    <>
                        <Typography sx={{
                            fontSize: '13px',
                            color: '#6B7280',
                            fontFamily: 'Inter, sans-serif',
                            lineHeight: '1.5',
                            marginBottom: '12px',
                        }}>
                            Your recording has been uploaded. You can safely leave this page.
                        </Typography>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '10px 12px',
                            backgroundColor: '#F0FDF4',
                            borderRadius: '8px',
                        }}>
                            <CheckCircleIcon sx={{ fontSize: 16, color: '#22C55E' }} />
                            <Typography sx={{
                                fontSize: '13px',
                                fontWeight: 500,
                                color: '#166534',
                                fontFamily: 'Inter, sans-serif',
                            }}>
                                Done! You may close this tab.
                            </Typography>
                        </Box>
                    </>
                ) : (
                    <>
                        <Typography sx={{
                            fontSize: '13px',
                            color: '#6B7280',
                            fontFamily: 'Inter, sans-serif',
                            lineHeight: '1.5',
                            marginBottom: '12px',
                        }}>
                            Keep this page open while your recording uploads.
                        </Typography>
                        
                        {/* Progress section */}
                        <Box sx={{
                            padding: '12px',
                            backgroundColor: '#F9FAFB',
                            borderRadius: '8px',
                        }}>
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                marginBottom: '8px',
                            }}>
                                <Typography sx={{
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    color: '#374151',
                                    fontFamily: 'Inter, sans-serif',
                                }}>
                                    Progress
                                </Typography>
                                {showProgress ? (
                                    <Typography sx={{
                                        fontSize: '13px',
                                        fontWeight: 500,
                                        color: '#6B7280',
                                        fontFamily: 'Inter, sans-serif',
                                    }}>
                                        {Math.round(displayProgress)}%
                                    </Typography>
                                ) : (
                                    <Box sx={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: '50%',
                                        border: '2px solid #E5E7EB',
                                        borderTopColor: '#9CA3AF',
                                        animation: 'spin 1s linear infinite',
                                        '@keyframes spin': {
                                            '0%': { transform: 'rotate(0deg)' },
                                            '100%': { transform: 'rotate(360deg)' },
                                        },
                                    }} />
                                )}
                            </Box>
                            <LinearProgress
                                variant={showProgress ? "determinate" : "indeterminate"}
                                value={displayProgress}
                                sx={{
                                    height: 4,
                                    borderRadius: 2,
                                    backgroundColor: '#E5E7EB',
                                    '& .MuiLinearProgress-bar': {
                                        backgroundColor: '#2B6BFD',
                                        borderRadius: 2,
                                    },
                                }}
                            />
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    );
};

/**
 * Mini indicator to show upload status when overlay is closed
 * Allows users to re-open the full overlay
 */
interface UploadStatusIndicatorProps {
    isUploading: boolean;
    isComplete: boolean;
    onClick: () => void;
}

export const UploadStatusIndicator: React.FC<UploadStatusIndicatorProps> = ({
    isUploading,
    isComplete,
    onClick,
}) => {
    if (!isUploading && !isComplete) return null;

    return (
        <Box
            onClick={onClick}
            sx={{
                position: 'absolute',
                bottom: 24,
                left: 24,
                zIndex: 99,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 14px',
                borderRadius: '20px',
                backgroundColor: '#ffffff',
                boxShadow: '0px 4px 20px rgba(15, 23, 42, 0.08)',
                border: '1px solid #E5E7EB',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                '&:hover': {
                    backgroundColor: '#F9FAFB',
                    boxShadow: '0px 6px 24px rgba(15, 23, 42, 0.12)',
                },
            }}
        >
            {isComplete ? (
                <CheckCircleIcon sx={{ fontSize: 16, color: '#22C55E' }} />
            ) : (
                <Box sx={{
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    border: '2px solid #E5E7EB',
                    borderTopColor: '#2B6BFD',
                    animation: 'spin 1s linear infinite',
                    '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                    },
                }} />
            )}
            <Typography sx={{
                fontSize: '12px',
                fontWeight: 500,
                color: isComplete ? '#166534' : '#374151',
                fontFamily: 'Inter, sans-serif',
            }}>
                {isComplete ? 'Uploads complete' : 'Uploading...'}
            </Typography>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: '2px',
            }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <path d="M7 17L17 7M17 7H8M17 7V16" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </Box>
        </Box>
    );
};

export default HostRecordingStoppedOverlay;
